import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { GlobalHotKeys } from 'react-hotkeys';

import { Paper } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';

import { RootState } from 'state/store';
import { selectLeadFullName } from 'state/lead.state';
import { apiInitiateCallCenterFlow, apiAcceptInboundCall } from 'state/call.state';
import { CallTypes, Colors } from 'typings';

import OnCall from './CallPanel/OnCall';
import CallLead from './CallPanel/CallLead';
import AnswerCall from './CallPanel/AnswerCall';
import InboundCallGreeting from './CallPanel/InboundCallGreeting';
import ActionsPanel from './ActionsPanel';
import twilioService from 'services/twilio.service';
import { isCallCenterTransferRestricted, isWithinBusinessHours } from 'utils/functions';

const audioSrc = require('assets/sounds/ringing.mp3');

const getPanelBackground = (props: { [key: string]: any }, theme: Theme) => {
  if (props.isReceivingInboundCall) {
    return `transparent linear-gradient(90deg, #F24689 0%, #4397ED 100%) 0% 0% no-repeat padding-box`;
  }
  if (props.isOnCall) {
    return `transparent linear-gradient(90deg, ${theme.palette.background.green} 0%, ${theme.palette.primary.main} 100%) 0% 0% no-repeat padding-box`;
  }
  if (!props.hasCallAbility) {
    return Colors.LIGHT_GRAY;
  }
  if (props.isCallCenterRoutingEnabled) {
    return Colors.PURPLE;
  }

  return theme.palette.primary.main;
};

const useStyles = makeStyles((theme: Theme) => ({
  callPanelWrapper: (props: any) => ({
    position: 'relative',
    color: Colors.WHITE,
    background: getPanelBackground(props, theme),
    padding: theme.spacing(1.5),
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  }),
}));

const LeadActionsPanel = () => {
  const dispatch = useDispatch();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lead = useSelector((state: RootState) => state.lead.entity);
  const concierge = useSelector((state: RootState) => state.concierge.entity);
  const leadName = useSelector(selectLeadFullName);
  const isOnCall = useSelector((state: RootState) => state.concierge.isOnCall);
  const isInboundCall = useSelector(
    (state: RootState) => state.call.callType === CallTypes.INBOUND
  );
  const isReceivingInboundCall = useSelector((state: RootState) => state.call.incomingCall);
  const isCallCenterRoutingEnabled = useSelector(
    (state: RootState) =>
      !!state.lead.entity?.agent?.agentSetting?.settings?.inboundCalls?.callCenterRouting?.enabled
  );

  const [isAnswering, setIsAnswering] = useState(false);
  const [isDialing, setIsDialing] = useState(false);
  const isFetchingCallAccessToken = useSelector(
    (state: RootState) => state.concierge.isFetchingCallAccessToken
  );

  const isEngaged = isOnCall || isReceivingInboundCall;
  const showGreeting = (isOnCall && isInboundCall) || isReceivingInboundCall;
  const hasCallAbility = isCallCenterRoutingEnabled || !!concierge?.canCall;
  const canMakeCallCenterCall =
    lead?.agent &&
    (!isCallCenterTransferRestricted(lead.agent) || isWithinBusinessHours(lead.agent));
  const disableCall =
    !lead?.phone ||
    !hasCallAbility ||
    (isCallCenterRoutingEnabled && !canMakeCallCenterCall) ||
    isDialing ||
    isFetchingCallAccessToken;

  const classes = useStyles({
    isOnCall,
    isReceivingInboundCall,
    hasCallAbility,
    isCallCenterRoutingEnabled,
  });

  const playIbcAudio = () => {
    if (!audioRef?.current) {
      audioRef.current = new Audio(audioSrc);
    }

    audioRef.current.play();
  };

  const stopIbcAudio = () => {
    if (audioRef?.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const callLead = () => {
    if (lead && concierge) {
      if (isCallCenterRoutingEnabled) {
        if (
          lead?.agent &&
          (!isCallCenterTransferRestricted(lead.agent) || isWithinBusinessHours(lead.agent))
        ) {
          setIsDialing(true);
          dispatch(apiInitiateCallCenterFlow({ leadId: lead.id }));
        }

        return;
      }

      twilioService.dialOutbound(lead, concierge);
    }
  };

  const answerCall = () => {
    stopIbcAudio();
    setIsAnswering(true);
    dispatch(apiAcceptInboundCall());
  };

  const hotKeyHandlers = {
    CALL: callLead,
  };

  // ensure call is not active after this component is unmounted
  useEffect(() => {
    return () => {
      twilioService.endCall();
    };
  }, []);

  useEffect(() => {
    if (isReceivingInboundCall) {
      playIbcAudio();
    } else {
      stopIbcAudio();
    }
  }, [isReceivingInboundCall]);

  return (
    <div>
      <ActionsPanel />
      <Paper className={classes.callPanelWrapper} elevation={0}>
        {showGreeting && <InboundCallGreeting />}
        {!isEngaged && (
          <>
            <CallLead
              onClick={callLead}
              lead={leadName}
              disable={disableCall}
              isCallCenterRoutingEnabled={isCallCenterRoutingEnabled}
            />
            <GlobalHotKeys handlers={hotKeyHandlers} />
          </>
        )}
        {isReceivingInboundCall && (
          <AnswerCall
            onClick={answerCall}
            disabled={isAnswering || isFetchingCallAccessToken}
            label={isAnswering ? 'Connecting...' : 'Answer Call'}
          />
        )}
        {isOnCall && <OnCall />}
      </Paper>
    </div>
  );
};

export default LeadActionsPanel;
